import React, { useCallback, useState } from "react";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";
import { useBlocks, useUi } from "@jelly/lesson";
import { Accordion, AccordionItem } from "@src/lms/jelly/extensions/ui";
import { useIntl } from "react-intl";

import { messages } from "./CreativeProcess.messages";
import styles from "./CreativeProcess.module.scss";

import * as appConfig from "appConfig";
import { JuliusBioModal } from "@src/lms/jelly/extensions/layouts/_lesson/case2/creative-process/components/JuliusBioModal";
import { MichaelBioModal } from "@src/lms/jelly/extensions/layouts/_lesson/case2/creative-process/components/MichaelBioModal";
import { AnaBioModal } from "@src/lms/jelly/extensions/layouts/_lesson/case2/creative-process/components/AnaBioModal";
import { DanielBioModal } from "@src/lms/jelly/extensions/layouts/_lesson/case2/creative-process/components/DanielBioModal";
import { SpatialCarousel } from "../../..";
import { PhotoView } from "../../../photo-view/PhotoView";

interface CreativeProcess {
  title: string;
}

export const CreativeProcess: React.FC<CreativeProcess> = ({ title }) => {
  return (
    <Accordion allowMultiOpen={false}>
      <CreativeProcessContent title={title} />
    </Accordion>
  );
};

const CreativeProcessContent: React.FC<CreativeProcess> = ({ title }) => {
  const ui = useUi();
  const blocks = useBlocks();
  const { formatMessage } = useIntl();
  const [juliusModalOpen, setJuliusModalOpen] = useState(false);
  const [michaelModalOpen, setMichaelModalOpen] = useState(false);
  const [anaModalOpen, setAnaModalOpen] = useState(false);
  const [danielModalOpen, setDanielModalOpen] = useState(false);

  const handleJuliusModalToggle = useCallback(() => setJuliusModalOpen((open) => !open), []);
  const handleMichaelModalToggle = useCallback(() => setMichaelModalOpen((open) => !open), []);
  const handleAnaModalToggle = useCallback(() => setAnaModalOpen((open) => !open), []);
  const handleDanielModalToggle = useCallback(() => setDanielModalOpen((open) => !open), []);

  const accordion = (
    <div className={styles.accordion}>
      <ui.Scrollbars>
        <AccordionItem id="julius" title={formatMessage(messages.accordionItem1Title)} unmountChildrenWhenHidden>
          <div style={{ width: "100%", height: "142px" }}>
            <blocks.image src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case02/creative-process/1.jpg`} />
          </div>
          <p>{formatMessage(messages.accordionItem1Description)}</p>
          <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleJuliusModalToggle}>
            {formatMessage(messages.bioNote)}
          </ui.Button>
        </AccordionItem>
        <AccordionItem id="michael" title={formatMessage(messages.accordionItem2Title)} unmountChildrenWhenHidden>
          <div style={{ width: "100%", height: "142px" }}>
            <blocks.image src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case02/creative-process/2.jpg`} />
          </div>
          <p>{formatMessage(messages.accordionItem2Description)}</p>
          <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleMichaelModalToggle}>
            {formatMessage(messages.bioNote)}
          </ui.Button>
        </AccordionItem>
        <AccordionItem id="ana" title={formatMessage(messages.accordionItem3Title)} unmountChildrenWhenHidden>
          <div style={{ width: "100%", height: "142px" }}>
            <blocks.image src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case02/creative-process/3.jpg`} />
          </div>
          <p>{formatMessage(messages.accordionItem3Description)}</p>
          <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleAnaModalToggle}>
            {formatMessage(messages.bioNote)}
          </ui.Button>
        </AccordionItem>
        <AccordionItem id="daniel" title={formatMessage(messages.accordionItem4Title)} unmountChildrenWhenHidden>
          <div style={{ width: "100%", height: "142px" }}>
            <blocks.image src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case02/creative-process/4.jpg`} />
          </div>
          <p>{formatMessage(messages.accordionItem4Description)}</p>
          <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleDanielModalToggle}>
            {formatMessage(messages.bioNote)}
          </ui.Button>
        </AccordionItem>
      </ui.Scrollbars>
    </div>
  );

  return (
    <StandardCase title={title} leftComponent={accordion}>
      <div className={styles.gallery}>
        {/*// @ts-expect-error We cannot override layouts using Contexts :/*/}
        <SpatialCarousel controlsPosition={"bottom"}>
          {[
            { src: "1.jpg", description: formatMessage(messages.photoDescription1) },
            { src: "2.jpg", description: formatMessage(messages.photoDescription1) },
            { src: "3.jpg", description: formatMessage(messages.photoDescription1) },
            { src: "4.jpg", description: formatMessage(messages.photoDescription2) },
            { src: "5.jpg", description: formatMessage(messages.photoDescription1) },
            { src: "6.jpg", description: formatMessage(messages.photoDescription1) },
          ].map((item) => (
            <PhotoView
              key={item.src}
              imgSrc={`${appConfig.STATIC_ASSETS_URL}/lms/case/case02/creative-process/slide-show/${item.src}`}
              description={item.description}
              imgMode={"contain"}
            />
          ))}
        </SpatialCarousel>
      </div>
      <JuliusBioModal visible={juliusModalOpen} onClose={handleJuliusModalToggle} />
      <MichaelBioModal visible={michaelModalOpen} onClose={handleMichaelModalToggle} />
      <AnaBioModal visible={anaModalOpen} onClose={handleAnaModalToggle} />
      <DanielBioModal visible={danielModalOpen} onClose={handleDanielModalToggle} />
    </StandardCase>
  );
};
